/**
 * Client-side email HTML builder mirroring the server's buildEmailFromTemplate.
 * Used for instant live preview without server calls.
 */

const sampleData = {
    otp: { code: '482916' },
    setup_submission: { groupName: 'Marketing Team', companyName: 'Acme Corp' },
    reviewee_launch: { firstName: 'Sarah', loginUrl: 'https://app.example.com' },
    reviewer_launch: {
        firstName: 'John',
        revieweeFirstName: 'Sarah',
        revieweeLastName: 'Johnson',
        directLink: 'https://app.example.com/360/review/abc123',
        deadline: 'March 15, 2026'
    },
    reminder: {
        firstName: 'John',
        revieweeFirstName: 'Sarah',
        revieweeLastName: 'Johnson',
        directLink: 'https://app.example.com/360/review/abc123',
        deadline: 'March 15, 2026'
    },
    results_ready: { firstName: 'Sarah', loginUrl: 'https://app.example.com' },
    tim_results_ready: { firstName: 'Sarah', loginUrl: 'https://app.example.com' }
};

const replacePlaceholders = (text, variables) => {
    if (!text) return '';
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
        return variables[key] !== undefined ? variables[key] : `{{${key}}}`;
    });
};

const getSectionStyle = (style) => {
    switch (style) {
        case 'instruction':
            return 'color: #666; font-size: 14px; margin-bottom: 5px;';
        case 'note':
            return 'color: #999; font-size: 14px; margin-top: 20px;';
        default:
            return 'color: #666; font-size: 16px; margin-bottom: 10px;';
    }
};

export const buildPreviewHtml = (template) => {
    if (!template) return '';

    const variables = sampleData[template.templateKey] || {};
    const heading = replacePlaceholders(template.heading, variables);
    const headingColor = template.headingColor || '#333';

    let subHeadingHtml = '';
    if (template.subHeading) {
        const subColor = template.subHeadingColor || '#007bff';
        subHeadingHtml = `<h2 style="color: ${subColor}; margin-bottom: 20px; text-align: center;">${replacePlaceholders(template.subHeading, variables)}</h2>`;
    }

    const bodySectionsHtml = (template.bodySections || []).map(section => {
        const style = getSectionStyle(section.style);
        return `<p style="${style}">${replacePlaceholders(section.text, variables)}</p>`;
    }).join('\n                ');

    let instructionsHtml = '';
    if (template.instructions && template.instructions.length > 0) {
        const instrLabel = `<p style="color: #666; font-size: 14px; margin-bottom: 5px;"><strong>To get started:</strong></p>`;
        const instrItems = template.instructions.map((instr, i) => {
            const isLast = i === template.instructions.length - 1;
            return `<p style="color: #666; font-size: 14px; margin-bottom: ${isLast ? '25' : '3'}px;">${replacePlaceholders(instr, variables)}</p>`;
        }).join('\n                ');
        instructionsHtml = `${instrLabel}\n                ${instrItems}`;
    }

    let ctaHtml = '';
    if (template.templateKey === 'otp' && variables.code) {
        ctaHtml = `
                <div style="background-color: #fff; border: 2px solid #007bff; border-radius: 8px; padding: 20px; margin: 20px 0;">
                    <span style="font-size: 32px; font-weight: bold; letter-spacing: 8px; color: #007bff;">
                        ${variables.code}
                    </span>
                </div>`;
    } else if (template.ctaButtonText) {
        const btnColor = template.ctaButtonColor || '#007bff';
        const btnText = replacePlaceholders(template.ctaButtonText, variables);
        const btnUrl = variables.directLink || variables.loginUrl || '#';
        ctaHtml = `
                <div style="text-align: center;">
                    <a href="${btnUrl}" style="display: inline-block; background-color: ${btnColor}; color: #fff; text-decoration: none; padding: 12px 30px; border-radius: 5px; font-size: 16px;">
                        ${btnText}
                    </a>
                </div>`;
    }

    let deadlineHtml = '';
    if (template.hasConditionalDeadline && variables.deadline) {
        deadlineHtml = `<p style="color: #666; font-size: 16px; margin-bottom: 20px;">${replacePlaceholders(template.deadlineText, variables)}</p>`;
    } else if (template.hasConditionalDeadline && !variables.deadline && template.templateKey === 'reviewer_launch') {
        deadlineHtml = `<p style="color: #666; font-size: 16px; margin-bottom: 20px;">The assessment takes approximately 15&ndash;20 minutes to complete.</p>`;
    }

    const postCtaHtml = (template.postCtaSections || []).map(section => {
        const style = getSectionStyle(section.style);
        return `<p style="${style}">${replacePlaceholders(section.text, variables)}</p>`;
    }).join('\n                ');

    let closingHtml = '';
    if (template.closingText) {
        const closingLines = template.closingText.split('\n').join('<br>');
        closingHtml = `<p style="color: #999; font-size: 12px; margin-top: 25px;">${replacePlaceholders(closingLines, variables)}</p>`;
    }

    const textAlign = template.templateKey === 'otp' || template.templateKey === 'setup_submission' ? 'text-align: center;' : '';

    return `<!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background-color: #f8f9fa; border-radius: 10px; padding: 30px; ${textAlign}">
                <h1 style="color: ${headingColor}; margin-bottom: 20px;${textAlign ? '' : ' text-align: center;'}">${heading}</h1>
                ${subHeadingHtml}
                ${bodySectionsHtml}
                ${instructionsHtml}
                ${ctaHtml}
                ${deadlineHtml}
                ${postCtaHtml}
                ${closingHtml}
            </div>
        </body>
        </html>`;
};

export { sampleData };
